import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Progress } from "@shared/components/ui/progress";
import {
  CheckCircle,
  Clock,
  MapPin,
  Package,
  Truck,
  Warehouse,
  AlertCircle,
  User,
} from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

const PICKUP_STATUS_CONFIG = {
  order_received: {
    label: "pickupTracking.pickupStatuses.order_received",
    icon: Clock,
    color: "bg-blue-500",
    textColor: "text-blue-600",
    bgColor: "bg-blue-50",
    order: 0,
  },
  order_confirmed: {
    label: "pickupTracking.pickupStatuses.order_confirmed",
    icon: CheckCircle,
    color: "bg-green-500",
    textColor: "text-green-600",
    bgColor: "bg-green-50",
    order: 1,
  },
  to_pick_up: {
    label: "pickupTracking.pickupStatuses.to_pick_up",
    icon: MapPin,
    color: "bg-yellow-500",
    textColor: "text-yellow-600",
    bgColor: "bg-yellow-50",
    order: 2,
  },
  picked_up: {
    label: "pickupTracking.pickupStatuses.picked_up",
    icon: Package,
    color: "bg-blue-600",
    textColor: "text-blue-600",
    bgColor: "bg-blue-50",
    order: 3,
  },
  in_transit: {
    label: "pickupTracking.pickupStatuses.in_transit",
    icon: Truck,
    color: "bg-purple-500",
    textColor: "text-purple-600",
    bgColor: "bg-purple-50",
    order: 4,
  },
  arrived_at_warehouse: {
    label: "pickupTracking.pickupStatuses.arrived_at_warehouse",
    icon: Warehouse,
    color: "bg-indigo-500",
    textColor: "text-indigo-600",
    bgColor: "bg-indigo-50",
    order: 5,
  },
  signed: {
    label: "pickupTracking.pickupStatuses.signed",
    icon: CheckCircle,
    color: "bg-green-600",
    textColor: "text-green-600",
    bgColor: "bg-green-50",
    order: 6,
  },
  return_collection: {
    label: "pickupTracking.pickupStatuses.return_collection",
    icon: AlertCircle,
    color: "bg-red-500",
    textColor: "text-red-600",
    bgColor: "bg-red-50",
    order: 6,
  },
};

export function PickupTrackingTimeline({ pickup, className = "" }) {
  const { t } = useTranslation();

  const { progress, timelineSteps } = useMemo(() => {
    if (!pickup || !pickup.statusHistory) {
      return { progress: 0, timelineSteps: [] };
    }

    const currentStatus = pickup.status;
    const currentConfig = PICKUP_STATUS_CONFIG[currentStatus];
    const progress = currentConfig
      ? Math.round((currentConfig.order / 6) * 100)
      : 0;

    // Sort status history by date
    const sortedHistory = [...pickup.statusHistory].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    // Create timeline steps with completed states
    const allStatuses = [
      "order_received",
      "order_confirmed",
      "to_pick_up",
      "picked_up",
      "in_transit",
      "arrived_at_warehouse",
      "signed",
    ];

    // If status is return_collection, show it as the final step
    if (currentStatus === "return_collection") {
      allStatuses.push("return_collection");
    }

    const timelineSteps = allStatuses.map((status) => {
      const config = PICKUP_STATUS_CONFIG[status];
      const historyEntry = sortedHistory.find((h) => h.status === status);
      const isCompleted = historyEntry !== undefined;
      const isCurrent = status === currentStatus;
      const isFailed = status === "return_collection";

      return {
        status,
        config,
        historyEntry,
        isCompleted,
        isCurrent,
        isFailed,
      };
    });

    return { progress, timelineSteps };
  }, [pickup]);

  const formatDateTime = (date) => {
    return format(new Date(date), "MMM dd, yyyy HH:mm");
  };

  if (!pickup) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            {t("pickupTracking.noPickupData")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("pickupTracking.timeline")}
          </span>
          <Badge variant="outline" className="flex items-center gap-1">
            {PICKUP_STATUS_CONFIG[pickup.status]?.icon &&
              React.createElement(PICKUP_STATUS_CONFIG[pickup.status].icon, {
                className: "h-3 w-3",
              })}
            {t(PICKUP_STATUS_CONFIG[pickup.status]?.label || pickup.status)}
          </Badge>
        </CardTitle>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>{t("pickupTracking.progress")}</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Timeline */}
        <div className="relative">
          {timelineSteps.map((step, index) => {
            const {
              status,
              config,
              historyEntry,
              isCompleted,
              isCurrent,
              isFailed,
            } = step;
            const Icon = config.icon;

            return (
              <div
                key={status}
                className="relative flex items-start pb-6 last:pb-0"
              >
                {/* Timeline line */}
                {index < timelineSteps.length - 1 && (
                  <div
                    className={`absolute left-4 top-8 w-0.5 h-full ${
                      isCompleted && !isFailed ? "bg-green-200" : "bg-gray-200"
                    }`}
                  />
                )}

                {/* Timeline icon */}
                <div
                  className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                    isCompleted && !isFailed
                      ? "border-green-500 bg-green-500 text-white"
                      : isCurrent && isFailed
                      ? "border-red-500 bg-red-500 text-white"
                      : isCurrent
                      ? "border-blue-500 bg-blue-500 text-white"
                      : "border-gray-300 bg-white text-gray-400"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>

                {/* Timeline content */}
                <div className="ml-4 flex-1">
                  <div
                    className={`rounded-lg p-4 ${
                      isCompleted || isCurrent
                        ? isFailed
                          ? "bg-red-50"
                          : config.bgColor
                        : "bg-gray-50"
                    }`}
                  >
                    {/* Status title */}
                    <div className="flex items-center justify-between">
                      <h4
                        className={`font-medium ${
                          isCompleted || isCurrent
                            ? isFailed
                              ? "text-red-600"
                              : config.textColor
                            : "text-gray-600"
                        }`}
                      >
                        {t(config.label)}
                      </h4>
                      {historyEntry && (
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(historyEntry.date)}
                        </span>
                      )}
                    </div>

                    {/* Status details */}
                    {historyEntry && (
                      <div className="mt-2 space-y-2">
                        {historyEntry.note && (
                          <p className="text-sm text-muted-foreground">
                            {historyEntry.note}
                          </p>
                        )}
                        {historyEntry.user && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>
                              {t("pickupTracking.updatedBy")}:{" "}
                              {historyEntry.user.fullName ||
                                historyEntry.user.name}
                              {historyEntry.user.role &&
                                ` (${t(
                                  `user.role.${historyEntry.user.role}`
                                )})`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Current status indicator */}
                    {isCurrent && !isCompleted && (
                      <div className="mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {t("pickupTracking.currentStatus")}
                        </Badge>
                      </div>
                    )}

                    {/* Future status indicator */}
                    {!isCompleted && !isCurrent && (
                      <div className="mt-2">
                        <span className="text-xs text-muted-foreground">
                          {t("pickupTracking.pending")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Information */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">{t("Created")}: </span>
              <span className="text-muted-foreground">
                {formatDateTime(pickup.createdAt)}
              </span>
            </div>
            <div>
              <span className="font-medium">{t("Last Updated")}: </span>
              <span className="text-muted-foreground">
                {formatDateTime(pickup.updatedAt)}
              </span>
            </div>
            <div>
              <span className="font-medium">{t("Total Steps")}: </span>
              <span className="text-muted-foreground">
                {pickup.statusHistory?.length || 0}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PickupTrackingTimeline;
